import { Box, HStack, Image, Text, VStack, chakra } from '@chakra-ui/react';
import { LuCheck, LuChevronDown, LuPlus } from 'react-icons/lu';
import type { IconType } from 'react-icons';
import { MenuContent, MenuItem, MenuRoot, MenuSeparator, MenuTrigger } from '@/components/ui/menu';

/**
 * The two places a workspace switcher appears.
 *
 * `hero` is the hub's own — a sentence with the name underlined inside it.
 * `crumb` is breadcrumb 1, which used to be a separate menu listing homes *and*
 * groups under one chevron; conflating "switch product" with "switch workspace"
 * is exactly what `ProductSwitcher` was built to stop, so the crumb now opens
 * the same list as the hero above it.
 */
export type SwitcherVariant = 'hero' | 'crumb';

export interface SwitcherItem {
    id: string;
    label: string;
    /** The second line — "Sonoma, CA · 4 members", "5 members · 12 saved". */
    meta?: React.ReactNode;
    /** Artwork for the row's tile; a hatched placeholder stands in without one. */
    artwork?: string;
}

interface SwitcherMenuProps {
    variant: SwitcherVariant;
    /** Reads before the name in the hero — "Here's what's happening at". */
    prefix?: React.ReactNode;
    /** The crumb draws one; the hero does not. */
    icon?: IconType;
    /** The workspace being named, and the row that carries the check mark. */
    currentId: string;
    currentLabel: string;
    items: SwitcherItem[];
    onSelect: (id: string) => void;
    /** The "Add home" / "Start a new group" row under the separator. */
    addLabel: string;
    onAdd: () => void;
}

/**
 * Which workspace you are looking at, and how to leave it.
 *
 * The shell of the control only — homes and groups differ in what fills the
 * rows, not in how the menu behaves, so `HomeSwitcher` and `GroupSwitcher` map
 * their records onto `SwitcherItem` and hand them over.
 */
export function SwitcherMenu({
    variant,
    prefix,
    icon: Icon,
    currentId,
    currentLabel,
    items,
    onSelect,
    addLabel,
    onAdd,
}: SwitcherMenuProps) {
    const isCrumb = variant === 'crumb';

    const trigger = isCrumb ? (
        <chakra.button
            type="button"
            display="flex"
            alignItems="center"
            gap="2"
            ml="-2"
            px="2"
            py="1"
            borderRadius="md"
            bg="transparent"
            cursor="pointer"
            maxW="md"
            minW={0}
            color="fg.muted"
            _hover={{ bg: 'blackAlpha.50', _dark: { bg: 'whiteAlpha.100' } }}
            _focusVisible={{ outline: '2px solid', outlineColor: 'brand.500', outlineOffset: '1px' }}
        >
            {Icon && (
                <Box color="fg.subtle" flexShrink={0} aria-hidden>
                    <Icon size={13} />
                </Box>
            )}
            <Text fontSize="sm" fontWeight="medium" truncate title={currentLabel}>
                {currentLabel}
            </Text>
            <Box color="fg.subtle" flexShrink={0} aria-hidden>
                <LuChevronDown size={11} />
            </Box>
        </chakra.button>
    ) : (
        <chakra.button
            type="button"
            display="flex"
            alignItems="center"
            gap="7px"
            textAlign="left"
            bg="transparent"
            borderWidth={0}
            p={0}
            cursor="pointer"
            borderRadius="md"
            minW={0}
            _focusVisible={{ outline: '2px solid', outlineColor: 'gold.solid', outlineOffset: '3px' }}
        >
            {prefix && (
                <Text as="span" fontSize="16px" fontWeight="medium" color="fg.nav" flexShrink={0}>
                    {prefix}
                </Text>
            )}
            <Text
                as="span"
                fontSize="16px"
                fontWeight="semibold"
                color="fg"
                borderBottomWidth="1px"
                borderColor="border.emphasized"
                truncate
            >
                {currentLabel}
            </Text>
            <Box as="span" color="fg.nav" display="flex" flexShrink={0}>
                <LuChevronDown size={13} strokeWidth={2.2} />
            </Box>
        </chakra.button>
    );

    return (
        <MenuRoot>
            <MenuTrigger asChild>{trigger}</MenuTrigger>
            <MenuContent
                minW={isCrumb ? '264px' : '300px'}
                maxW="min(320px, calc(100vw - 24px))"
                borderRadius="xl"
            >
                <SwitcherMenuBody currentId={currentId} items={items} onSelect={onSelect} addLabel={addLabel} onAdd={onAdd} />
            </MenuContent>
        </MenuRoot>
    );
}

export interface SwitcherMenuBodyProps {
    currentId: string;
    items: SwitcherItem[];
    onSelect: (id: string) => void;
    addLabel: string;
    onAdd: () => void;
}

/**
 * The rows and the "Add" action, without the trigger or the root.
 *
 * Split out so the account menu can hang the same list off a submenu trigger
 * instead of its own `MenuRoot` — the flyout that opens on hover over the name.
 */
export function SwitcherMenuBody({ currentId, items, onSelect, addLabel, onAdd }: SwitcherMenuBodyProps) {
    return (
        <>
            {items.map((item) => (
                <SwitcherRow
                    key={item.id}
                    item={item}
                    current={item.id === currentId}
                    onSelect={() => onSelect(item.id)}
                />
            ))}

            <MenuSeparator />
            <MenuItem value="switcher-add" onClick={onAdd} cursor="button" aria-label={addLabel} borderRadius="md">
                <HStack gap={2}>
                    <Box color="gold.link" display="flex">
                        <LuPlus size={13} strokeWidth={2.4} />
                    </Box>
                    <Text fontSize="13px" fontWeight="semibold" color="gold.link">
                        {addLabel}
                    </Text>
                </HStack>
            </MenuItem>
        </>
    );
}

interface SwitcherRowProps {
    item: SwitcherItem;
    current: boolean;
    onSelect: () => void;
}

function SwitcherRow({ item, current, onSelect }: SwitcherRowProps) {
    return (
        <MenuItem value={`switcher-${item.id}`} onClick={onSelect} cursor="button" borderRadius="md">
            <HStack gap="11px" w="full" minW={0}>
                <Box
                    w="8"
                    h="10"
                    flexShrink={0}
                    overflow="hidden"
                    bg="bg.chip"
                    backgroundImage={
                        item.artwork
                            ? undefined
                            : 'repeating-linear-gradient(135deg, rgba(31,31,31,0.06) 0 5px, transparent 5px 10px)'
                    }
                >
                    {item.artwork && (
                        <Image src={item.artwork} alt="" w="full" h="full" objectFit="cover" loading="lazy" />
                    )}
                </Box>

                <VStack gap={0.5} align="flex-start" flex={1} minW={0}>
                    <Text fontSize="14px" fontWeight="semibold" color="fg" truncate maxW="full">
                        {item.label}
                    </Text>
                    {item.meta && (
                        <Text fontSize="12px" fontWeight="medium" color="fg.muted" truncate maxW="full">
                            {item.meta}
                        </Text>
                    )}
                </VStack>

                {current && (
                    <Box color="gold.solid" flexShrink={0} display="flex">
                        <LuCheck size={14} strokeWidth={2.4} />
                    </Box>
                )}
            </HStack>
        </MenuItem>
    );
}
